import type { Ref } from "@typegoose/typegoose";
import type { MapVertex, PolygonRings } from "../../utils/area.js";
import type { Organization } from "../organization/organization.js";
import { findOverlappingSites, findSiteContainingPoint } from "../site/utilities.js";
import { createDelayedRegistrationHook } from "./delayedRegistrationHook.js";

export type SiteBoundary = { verdiId: string; polygons: PolygonRings };

export type PositionedAsset = {
    latitude?: number;
    longitude?: number;
    position?: MapVertex;
    polygons?: PolygonRings;
    siteId?: string;
};

export const ownerSiteBoundaries =
    createDelayedRegistrationHook<(owner: Ref<Organization>) => Promise<SiteBoundary[]>>("ownerSiteBoundaries");

export const siteBoundaryChanged =
    createDelayedRegistrationHook<(owner: Ref<Organization>, siteId: string) => Promise<void>>("siteBoundaryChanged");

const findSite = (sites: readonly SiteBoundary[], asset: PositionedAsset): SiteBoundary | undefined => {
    if (asset.polygons) return findOverlappingSites(asset.polygons, sites).overlapping[0];
    // latitude/longitude win over position: the position sync save hook may not have run yet.
    const point =
        typeof asset.latitude === "number" && typeof asset.longitude === "number"
            ? { lat: asset.latitude, lng: asset.longitude }
            : asset.position;
    return point ? findSiteContainingPoint(sites, point) : undefined;
};

export const findAssetSiteId = (sites: readonly SiteBoundary[], asset: PositionedAsset): string | undefined => {
    const claimed = sites.find((site) => site.verdiId === asset.siteId);
    return ((claimed && findSite([claimed], asset)) ?? findSite(sites, asset))?.verdiId;
};

type SiteIdDocument = PositionedAsset & {
    owner?: Ref<Organization>;
    isModified: (paths: string[]) => boolean;
};

const SITE_ID_INPUT_PATHS = ["owner", "latitude", "longitude", "position", "polygons"];

export const siteIdSyncSaveHook: ["save", (this: SiteIdDocument) => Promise<void>] = [
    "save",
    async function (this: SiteIdDocument) {
        if (!this.isModified(SITE_ID_INPUT_PATHS)) return;
        this.siteId = findAssetSiteId(await ownerSiteBoundaries.function(this.owner as Ref<Organization>), this);
    },
];
